import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, Shield } from 'lucide-react';
import { requestOtp, verifyOtp, checkAccess } from '../services/api';
import { useAuthStore } from '../store/authStore';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.1 } }
};

interface TenantLoginPageProps {
  slug: string;
}

export default function TenantLoginPage({ slug }: TenantLoginPageProps) {
  const { login } = useAuthStore();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [codeChanged, setCodeChanged] = useState(false);

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await requestOtp(email.trim(), 'artist');

      const data = await res.json() as any;
      if (res.ok) {
        setMessage('If your email is registered, a code has been sent.');
        setStep('code');
      } else {
        setError(data.error || 'Failed to send code');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const autoSubmitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-submit when 6 digits are entered after 2s idle
  // Only auto-submit if the user has changed the code since the last error
  useEffect(() => {
    if (autoSubmitTimerRef.current) {
      clearTimeout(autoSubmitTimerRef.current);
      autoSubmitTimerRef.current = null;
    }
    if (code.length === 6 && step === 'code' && !loading && codeChanged) {
      autoSubmitTimerRef.current = setTimeout(() => {
        handleVerifyOtp();
      }, 2000);
    }
    return () => {
      if (autoSubmitTimerRef.current) {
        clearTimeout(autoSubmitTimerRef.current);
      }
    };
  }, [code, step, loading, codeChanged]);

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    setLoading(true);
    setCodeChanged(false);

    try {
      const res = await verifyOtp(email.trim(), code.trim(), 'artist');

      const data = await res.json() as any;
      if (res.ok && data.success) {
        const user = data.user;
        const resolvedSlug = user?.tenantSlug || slug;
        console.log('[Login] OTP verified, user:', user?.id, 'role:', user?.role, 'tenantSlug:', user?.tenantSlug);

        // Artist users: trust the backend-resolved slug, skip check-access
        if (user?.role === 'artist' && user?.tenantSlug) {
          console.log('[Login] Artist authenticated, redirecting to /dashboard');
          login(user, data.token);
          window.location.href = '/dashboard';
          return;
        }

        // Fallback: check access using the URL slug (admin or edge cases)
        console.log('[Login] Checking access for slug:', resolvedSlug, 'with fresh token');
        const accessRes = await checkAccess(resolvedSlug, data.token);

        if (!accessRes.ok) {
          console.error('[Login] check-access failed with status:', accessRes.status);
          setError('Unable to verify access. Please try again.');
          return;
        }

        const accessData = await accessRes.json() as any;
        console.log('[Login] check-access response:', JSON.stringify(accessData));

        if (!accessData.hasAccess) {
          console.error('[Login] check-access denied:', accessData.reason, '| slug:', resolvedSlug, '| user:', data.user);
          setError('Access denied. You are not the owner of this account.');
          return;
        }

        login(user, data.token);
        window.location.href = '/dashboard';
      } else {
        setError(data.error || 'Invalid code');
      }
    } catch (err) {
      console.error('[Login] error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-charcoal flex items-center justify-center px-4 py-16">
      <div className="absolute inset-0 waveform-bg opacity-20" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-electric-cyan/5 rounded-full blur-[120px]" />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="relative z-10 w-full max-w-md"
      >
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/fans-audio-logo.png" alt="fans.audio logo" className="w-8 h-8 object-contain" />
            <span className="font-mono font-bold text-lg">fans<span className="text-electric-cyan">.audio</span></span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-electric-cyan" />
            <h1 className="text-xl font-bold">Artist Login</h1>
          </div>
          <p className="text-text-secondary text-sm">Sign in to access your dashboard</p>
        </motion.div>

        {/* Form */}
        <motion.div variants={fadeInUp} className="glass-card p-6 sm:p-8">
          {step === 'email' ? (
            <form onSubmit={handleRequestOtp} className="space-y-5">
              <div>
                <label className="terminal-label block mb-2">email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors"
                  required
                  autoFocus
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email}
                className="btn-primary w-full !justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Send Login Code
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              {message && (
                <div className="p-3 rounded-lg bg-electric-cyan/10 border border-electric-cyan/20">
                  <p className="text-sm text-electric-cyan">{message}</p>
                </div>
              )}

              <div>
                <label className="terminal-label block mb-2">verification_code</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => {
                    const newCode = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setCode(newCode);
                    setCodeChanged(true);
                    setError(null);
                  }}
                  placeholder="000000"
                  className="w-full px-4 py-3 bg-surface border border-white/10 rounded-lg text-white placeholder-text-muted focus:border-electric-cyan/50 focus:outline-none focus:ring-1 focus:ring-electric-cyan/30 transition-colors text-center font-mono text-2xl tracking-[0.5em]"
                  required
                  autoFocus
                  maxLength={6}
                />
                <p className="mt-2 text-xs text-text-muted">Check your email for the 6-digit code</p>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="btn-primary w-full !justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Verify & Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => { setStep('email'); setCode(''); setError(null); setMessage(null); }}
                className="w-full text-center text-sm text-text-muted hover:text-electric-cyan transition-colors"
              >
                ← Use a different email
              </button>
            </form>
          )}
        </motion.div>

        <motion.p variants={fadeInUp} className="text-center text-xs text-text-muted mt-6 font-mono">
          &gt; artist access only. no passwords.
        </motion.p>
      </motion.div>
    </div>
  );
}
